import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, LibraryBig, User as UserIcon } from "lucide-react";

export default function UserMenu({ solid = true }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 pl-2 pr-3 py-1 border rounded-full transition-colors ${
          solid
            ? "border-border bg-white hover:border-ink"
            : "border-ivory/40 bg-ink/40 backdrop-blur text-ivory hover:border-ivory"
        }`}
      >
        <span
          className={`h-7 w-7 rounded-full inline-flex items-center justify-center font-serif text-sm ${
            solid ? "bg-ink text-ivory" : "bg-gold text-ivory"
          }`}
        >
          {initial}
        </span>
        <span className={`text-sm hidden sm:block max-w-[120px] truncate ${solid ? "text-ink" : "text-ivory"}`}>
          {user.name || user.email}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white border border-border shadow-xl z-50"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-border">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Signed in as
            </div>
            <div className="text-sm text-ink truncate">{user.email}</div>
          </div>
          <Link
            to="/dashboard/library"
            onClick={() => setOpen(false)}
            data-testid="user-menu-library"
            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary"
          >
            <LibraryBig className="h-4 w-4 text-gold" /> My library
          </Link>
          <Link
            to="/dashboard/library?tab=downloads"
            onClick={() => setOpen(false)}
            data-testid="user-menu-downloads"
            className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary"
          >
            <UserIcon className="h-4 w-4 text-gold" /> Downloads
          </Link>
          <button
            data-testid="user-menu-logout"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary border-t border-border"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
